import {
  FontColorEnum,
  FormField,
  FormHint,
  FormLabel,
  Select,
  SelectOptionProps,
  TIcon,
} from '@acceso/design-system';
import { ReactNode, useMemo, useState } from 'react';
import { Controller, FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// The design system's Select treats `null` as "no selection" and never matches it
// against options. We use this sentinel to represent the none option internally
// so the Select can highlight it, and convert null ↔ sentinel at the boundary.
const NONE_SENTINEL = '__select_none__';

type TSelectFormProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  options: SelectOptionProps[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  multiple?: boolean;
  additionalButtons?: {
    label: string;
    onClick: () => void;
    icon?: TIcon;
    disabled?: boolean;
  }[];
  classname?: string;
  handleChange?: (value: T) => void;
  noneOptionGender?: 'masculine' | 'feminine';
  defaultToNone?: boolean;
  onFilterTextChange?: (filterText: string) => void;
  withFilter?: boolean;
  hideLabelTag?: boolean;
  disableLocalFiltering?: boolean;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
  isLoadingMore?: boolean;
  onOpen?: () => void;
  emptyState?: ReactNode;
  /**
   * RISK: blocks the control without disabling the field. A disabled react-hook-form field drops
   * its value, so `disabled` here would erase the very value that is being named.
   */
  isLoading?: boolean;
};

export function SelectForm<T extends FieldValues>({
  label,
  name,
  options,
  placeholder,
  form,
  disabled = false,
  multiple = false,
  required,
  additionalButtons,
  classname,
  handleChange,
  noneOptionGender = 'masculine',
  defaultToNone = false,
  onFilterTextChange,
  withFilter,
  hideLabelTag = false,
  disableLocalFiltering = false,
  onEndReached,
  onEndReachedThreshold,
  isLoadingMore,
  onOpen,
  emptyState,
  isLoading = false,
}: TSelectFormProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const [filterText, setFilterText] = useState('');

  const handleFilterTextChange = (text: string) => {
    setFilterText(text);
    onFilterTextChange?.(text);
  };

  // `Select` empties its search box on opening without reporting it, so the term is reset here.
  const handleOpen = () => {
    setFilterText('');
    onOpen?.();
  };

  const labelSuffix = required ? '*' : '';
  const noneOptionLabel = noneOptionGender === 'masculine' ? t('Ninguno') : t('Ninguna');
  // With server search nothing filters the none option out locally, so it is left out by hand.
  const hidesNoneOption = required || (disableLocalFiltering && filterText !== '');
  const selectOptions: SelectOptionProps<any>[] = useMemo(() => {
    if (options.length === 0) {
      return [];
    }

    if (hidesNoneOption) {
      return options;
    }

    return [{ label: noneOptionLabel, value: NONE_SENTINEL }, ...options];
  }, [hidesNoneOption, options, noneOptionLabel]);

  const searchProps = {
    onFilterTextChange: handleFilterTextChange,
    withFilter: disableLocalFiltering ? true : withFilter,
    disableLocalFiltering,
    onEndReached,
    onEndReachedThreshold,
    isLoadingMore,
    onOpen: handleOpen,
    emptyState,
  };

  return (
    <FormField className={classname}>
      {!hideLabelTag && (
        <FormLabel htmlFor={name}>
          {label}
          {labelSuffix}
        </FormLabel>
      )}
      <Controller
        disabled={disabled}
        control={control}
        name={name}
        defaultValue={defaultToNone ? (null as PathValue<T, Path<T>>) : undefined}
        rules={{
          validate: value => {
            if (!required) {
              return true;
            }

            if (value === undefined || value === null || value === '') {
              return t('El campo es obligatorio');
            }

            return true;
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          // Convert null (form value) → sentinel (for the Select to highlight the none option)
          const displayValue = !required && value === null ? NONE_SENTINEL : value;
          const hasValidValue =
            displayValue !== undefined &&
            selectOptions.some(option => option.value === displayValue);

          const handleSelectChange = (newValue: unknown) => {
            // Convert sentinel back to null before storing in the form
            const formValue = newValue === NONE_SENTINEL ? null : newValue;
            onChange(formValue);
            handleChange && handleChange(formValue as T);
          };

          return (
            <>
              {multiple ? (
                <Select
                  disabled={disabled || isLoading}
                  placeholder={placeholder || label}
                  value={hasValidValue ? value : []}
                  multiple={true}
                  onChange={(value: unknown) => {
                    onChange(value);
                    handleChange && handleChange(value as T);
                  }}
                  options={selectOptions}
                  hasError={!!error}
                  additionalButtons={additionalButtons}
                  {...searchProps}
                />
              ) : (
                <Select
                  disabled={disabled || isLoading}
                  placeholder={placeholder || label}
                  value={hasValidValue ? displayValue : null}
                  multiple={false}
                  onChange={handleSelectChange}
                  options={selectOptions}
                  hasError={!!error}
                  additionalButtons={additionalButtons}
                  {...searchProps}
                />
              )}
              {error && (
                <FormHint color={FontColorEnum.Error20} type="error">
                  {error.message}
                </FormHint>
              )}
            </>
          );
        }}
      />
    </FormField>
  );
}
